import React from "react";
import { BookingFormData, Doctor } from "../../types/BookingRv";

interface PatientFormProps {
  surname: string;
  setSurname: (value: string) => void;
  amka: string;
  setAmka: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  rvReason: string; // <--- Νέο prop
  setRvReason: (value: string) => void; // <--- Νέο prop
  doctorId: string;
  setDoctorId: (value: string) => void;
  appointmentDate: string;
  setAppointmentDate: (value: string) => void;
  doctors: Doctor[];
  loadingDoctors: boolean;
  selectedSlot: string | null;
  onSubmit: (formData: BookingFormData) => void;
}

function PatientForm({
  surname,
  setSurname,
  amka,
  setAmka,
  email,
  setEmail,
  rvReason,
  setRvReason,
  doctorId,
  setDoctorId,
  appointmentDate,
  setAppointmentDate,
  doctors,
  loadingDoctors,
  selectedSlot,
  onSubmit,
}: PatientFormProps): React.JSX.Element {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Προσθέσαμε και το rvReason στον έλεγχο υποχρεωτικών πεδίων
    if (
      !surname ||
      !amka ||
      !rvReason ||
      !doctorId ||
      !appointmentDate ||
      !selectedSlot
    ) {
      alert(
        "Παρακαλώ συμπληρώστε τα υποχρεωτικά πεδία, την ημερομηνία και επιλέξτε ραντεβού.",
      );
      return;
    }

    onSubmit({
      surname,
      amka,
      email,
      rvReason,
      doctorId,
      appointmentDate,
      selectedSlot,
    });
  };

  return (
    <div className="w-full rounded-lg border border-slate-200 shadow-sm bg-white p-6">
      <h2 className="text-base font-bold text-slate-700 uppercase tracking-wider mb-4 border-b border-slate-200 pb-3">
        Στοιχεία Ραντεβού Ασθενούς
      </h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Dropdown Επιλογής Γιατρού */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Επιλογή Γιατρού
          </label>
          <select
            value={doctorId}
            onChange={(e) => setDoctorId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-slate-800 shadow-sm"
            required
          >
            <option value="">-- Επιλέξτε Γιατρό --</option>
            {loadingDoctors ? (
              <option disabled>Φόρτωση γιατρών...</option>
            ) : (
              doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                  {doctor.specialtyDescr ? ` (${doctor.specialtyDescr})` : ""}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Πεδίο Ημερομηνίας */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Ημερομηνία Ραντεβού
          </label>
          <input
            type="date"
            value={appointmentDate}
            onChange={(e) => setAppointmentDate(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-slate-800 shadow-sm"
            required
          />
        </div>

        {/* Πεδίο Επώνυμο */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Επώνυμο
          </label>
          <input
            type="text"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            placeholder="Εισάγετε επώνυμο"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-slate-800 shadow-sm"
            required
          />
        </div>

        {/* Πεδίο ΑΜΚΑ */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            ΑΜΚΑ
          </label>
          <input
            type="text"
            value={amka}
            onChange={(e) => setAmka(e.target.value)}
            placeholder="Εισάγετε ΑΜΚΑ"
            maxLength={11}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-slate-800 shadow-sm"
            required
          />
        </div>

        {/* Πεδίο Λόγος Ραντεβού */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Λόγος Ραντεβού
          </label>
          <input
            type="text"
            value={rvReason}
            onChange={(e) => setRvReason(e.target.value)}
            placeholder="π.χ. Εξέταση / Συνταγογράφηση"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-slate-800 shadow-sm"
            required
          />
        </div>

        {/* Πεδίο Email (Προαιρετικό) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Email{" "}
            <span className="text-slate-400 font-normal">(προαιρετικό)</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@gmail.com"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-slate-800 shadow-sm"
          />
        </div>

        {/* Πληροφορίες επιλεγμένου ραντεβού */}
        <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-md">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
            Επιλεγμένη Ώρα Ραντεβού:
          </p>
          <p className="text-sm font-medium text-slate-800">
            {selectedSlot ? selectedSlot : "Δεν έχει επιλεγεί ώρα"}
          </p>
        </div>

        {/* Κουμπί υποβολής */}
        <button
          type="submit"
          className="mt-2 w-full py-2.5 px-4 bg-primary text-white font-medium text-sm rounded-md shadow-sm hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          Καταχώρηση Ραντεβού
        </button>
      </form>
    </div>
  );
}

export default PatientForm;
